import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Moon, Sun } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";

export function Wordmark() {
  return (
    <Link to="/" data-testid="wordmark-link" className="font-display text-lg font-semibold tracking-[0.18em] text-foreground">
      MIA
    </Link>
  );
}

export function ThemeToggle() {
  const { dark, toggle } = useTheme();
  return (
    <button
      type="button"
      data-testid="theme-toggle-button"
      onClick={toggle}
      aria-label="Toggle theme"
      className="rounded-full p-2 text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-foreground"
    >
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

export function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-border/60 py-8">
      <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-3 px-4 text-xs text-muted-foreground sm:flex-row sm:px-6 lg:px-8">
        <span>MIA — A Bauhaus Co. experiment</span>
        <span>MIA captures. MIA remembers. ChatGPT thinks.</span>
      </div>
    </footer>
  );
}

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", testId: "nav-dashboard-link" },
  { to: "/devices", label: "Devices", testId: "nav-devices-link" },
  { to: "/integrations", label: "Integrations", testId: "nav-integrations-link" },
  { to: "/privacy", label: "Privacy", testId: "nav-privacy-link" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header className="fixed left-0 right-0 top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Wordmark />
          <nav className="hidden items-center gap-1 md:flex" data-testid="main-nav">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `rounded-full px-3 py-1.5 text-sm transition-colors duration-200 ${
                    isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="nav-admin-link"
                className={({ isActive }) =>
                  `rounded-full px-3 py-1.5 text-sm transition-colors duration-200 ${
                    isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                Admin
              </NavLink>
            )}
          </nav>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Button variant="ghost" size="sm" data-testid="nav-sign-out-button" onClick={handleLogout}>
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <motion.main
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="mx-auto w-full max-w-5xl flex-1 px-4 pb-20 pt-28 sm:px-6 lg:px-8"
      >
        {children}
      </motion.main>
      <Footer />
    </div>
  );
}

export function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        <Wordmark />
        <ThemeToggle />
      </header>
      <motion.main
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 pb-20 sm:px-6"
      >
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
          <h1 className="font-display text-2xl font-medium tracking-tight" data-testid="auth-title">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
        {footer && <div className="mt-6 text-center text-sm text-muted-foreground">{footer}</div>}
      </motion.main>
      <Footer />
    </div>
  );
}
